// src/components/Layout/Header/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  FaFacebook, 
  FaInstagram, 
  FaWhatsapp, 
  FaLinkedinIn,
  FaBars, 
  FaTimes, 
  FaChevronDown 
} from 'react-icons/fa';
import LanguageSwitcher from '../../LanguageSwitcher/LanguageSwitcher';
import styles from './Header.module.css';

const Header = () => {
  const { t } = useTranslation(['common']);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTrainingDropdownOpen, setIsTrainingDropdownOpen] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsTrainingDropdownOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleTrainingDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTrainingDropdownOpen(!isTrainingDropdownOpen);
  };

  const handleSocialClick = (platform) => {
    const urls = {
      facebook: process.env.REACT_APP_FACEBOOK_URL || 'https://facebook.com/adtac.co',
      instagram: process.env.REACT_APP_INSTAGRAM_URL || 'https://instagram.com/adtac.co',
      whatsapp: `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || '60132228584'}`,
      linkedin: process.env.REACT_APP_LINKEDIN_URL || 'https://www.linkedin.com/company/ad-training-consultancy-adtac/',
    };
    window.open(urls[platform], '_blank');
  };

  // Navigation items with translation keys
  const navigationItems = [
    { name: t('navigation.home'), path: '/' },
    { name: t('navigation.about'), path: '/about-us' },
    { name: t('navigation.services'), path: '/consultancy-services' },
    {
      name: t('navigation.training'),
      path: '#',
      hasDropdown: true,
      dropdownItems: [
        { 
          name: t('navigation.foodHandler'), 
          path: 'https://kursuspengendalimakanan.com',
          isExternal: true 
        },
        { 
          name: t('navigation.managementSystem'), 
          path: '/management-system-training' 
        },
      ],
    },
    { name: t('navigation.gallery'), path: '/gallery' },
    { name: t('navigation.blog'), path: '/blog' },
    { name: t('navigation.contact'), path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
        <div className={styles.container}>
          {/* Logo Section */}
          <Link to="/" className={styles.logo}>
            <div className={styles.logoText}>
              <img 
                src="/assets/logo/HeaderFooter_logo.png" 
                alt="ADTAC knowledge.resolution" 
                className={styles.logoTextImage}
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className={styles.desktopNav}>
            <ul className={styles.navList}>
              {navigationItems.map((item, index) => (
                <li key={index} className={styles.navItem}>
                  {item.hasDropdown ? (
                    <div 
                      className={styles.dropdownContainer}
                      onMouseEnter={() => setIsTrainingDropdownOpen(true)}
                      onMouseLeave={() => setIsTrainingDropdownOpen(false)}
                    >
                      <button 
                        className={styles.navLink} 
                        onClick={toggleTrainingDropdown}
                      >
                        {item.name}
                        <FaChevronDown className={styles.dropdownIcon} />
                      </button>
                      
                      <AnimatePresence>
                        {isTrainingDropdownOpen && (
                          <motion.div
                            className={styles.dropdownMenu}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {item.dropdownItems.map((dropdownItem, idx) => (
                              dropdownItem.isExternal ? (
                                <a
                                  key={idx}
                                  href={dropdownItem.path}
                                  className={styles.dropdownItem}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {dropdownItem.name}
                                </a>
                              ) : (
                                <Link
                                  key={idx}
                                  to={dropdownItem.path}
                                  className={styles.dropdownItem}
                                >
                                  {dropdownItem.name}
                                </Link>
                              )
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link 
                      to={item.path} 
                      className={`${styles.navLink} ${isActive(item.path) ? styles.active : ''}`}
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Language Switcher and Social Icons */}
            <div className={styles.rightSection}>
              <LanguageSwitcher variant="desktop" />
              
              {/* Social Icons */}
              <div className={styles.socialIcons}>
                <button 
                  onClick={() => handleSocialClick('facebook')}
                  className={styles.socialIcon}
                  aria-label="Facebook"
                >
                  <FaFacebook />
                </button>
                <button 
                  onClick={() => handleSocialClick('instagram')}
                  className={styles.socialIcon}
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </button>
                <button 
                  onClick={() => handleSocialClick('linkedin')}
                  className={styles.socialIcon}
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn />
                </button>
                <button 
                  onClick={() => handleSocialClick('whatsapp')}
                  className={styles.socialIcon}
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp />
                </button>
              </div>
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <button 
            className={styles.mobileMenuToggle}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className={styles.mobileMenuBackdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMobileMenu}
            />

            {/* Mobile Menu Panel */}
            <motion.nav
              className={styles.mobileMenu}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              {/* Mobile Menu Header */}
              <div className={styles.mobileMenuHeader}>
                {/* Mobile Logo */}
                <Link to="/" className={styles.mobileLogo} onClick={toggleMobileMenu}>
                  <img 
                    src="/assets/logo/HeaderFooter_logo.png" 
                    alt="ADTAC Logo" 
                    className={styles.mobileLogoIcon}
                  />
                </Link>

                {/* Close Button */}
                <button 
                  className={styles.mobileMenuClose}
                  onClick={toggleMobileMenu}
                  aria-label="Close mobile menu"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Mobile Navigation Items */}
              <ul className={styles.mobileNavList}>
                {navigationItems.map((item, index) => (
                  <li key={index} className={styles.mobileNavItem}>
                    {item.hasDropdown ? (
                      <>
                        <button 
                          className={styles.mobileNavLink} 
                          onClick={toggleTrainingDropdown}
                        >
                          {item.name}
                          <FaChevronDown 
                            className={`${styles.mobileDropdownIcon} ${
                              isTrainingDropdownOpen ? styles.rotated : ''
                            }`} 
                          />
                        </button>
                        
                        <AnimatePresence>
                          {isTrainingDropdownOpen && (
                            <motion.div
                              className={styles.mobileDropdownMenu}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              {item.dropdownItems.map((dropdownItem, idx) => (
                                dropdownItem.isExternal ? (
                                  <a
                                    key={idx}
                                    href={dropdownItem.path}
                                    className={styles.mobileDropdownItem}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={toggleMobileMenu}
                                  >
                                    {dropdownItem.name}
                                  </a>
                                ) : (
                                  <Link
                                    key={idx}
                                    to={dropdownItem.path}
                                    className={styles.mobileDropdownItem}
                                    onClick={toggleMobileMenu}
                                  >
                                    {dropdownItem.name}
                                  </Link>
                                )
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link 
                        to={item.path} 
                        className={`${styles.mobileNavLink} ${
                          isActive(item.path) ? styles.active : ''
                        }`}
                        onClick={toggleMobileMenu}
                      >
                        {item.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>

              {/* Mobile Language Switcher */}
              <LanguageSwitcher variant="mobile" />

              {/* Mobile Social Icons */}
              <div className={styles.mobileSocialIcons}>
                <button 
                  onClick={() => handleSocialClick('facebook')}
                  className={styles.mobileSocialIcon}
                  aria-label="Facebook"
                >
                  <FaFacebook />
                </button>
                <button 
                  onClick={() => handleSocialClick('instagram')}
                  className={styles.mobileSocialIcon}
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </button>
                <button 
                  onClick={() => handleSocialClick('linkedin')}
                  className={styles.mobileSocialIcon}
                  aria-label="LinkedIn"
                >
                  <FaLinkedinIn />
                </button>
                <button 
                  onClick={() => handleSocialClick('whatsapp')}
                  className={styles.mobileSocialIcon}
                  aria-label="WhatsApp"
                >
                  <FaWhatsapp />
                </button>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;